import { useEffect, useRef, useCallback } from 'react';
import { storage } from '../services/storage/localStorage';

interface QuotaData {
  questionsUsedToday: number;
  questionsRemaining: number;
  dailyLimit: number;
}

interface UseQuotaSyncOptions {
  onQuotaUpdate: (data: QuotaData) => void;
  enabled: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Hook for real-time quota synchronization using Server-Sent Events (SSE)
 *
 * Automatically connects to the SSE endpoint and listens for quota updates.
 * When any user asks a question, all connected clients receive the updated quota immediately.
 */
export const useQuotaSync = ({ onQuotaUpdate, enabled }: UseQuotaSyncOptions) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    // Don't connect if disabled or already connected
    if (!enabled || eventSourceRef.current) {
      return;
    }

    const accessCode = storage.getAccessCode();
    if (!accessCode) {
      return;
    }

    try {
      // Create SSE connection with access code in URL query parameter
      // Note: EventSource doesn't support custom headers, so we pass via query param
      const url = `${API_URL}/api/quota/stream?access_code=${encodeURIComponent(accessCode)}`;
      const eventSource = new EventSource(url)

      eventSource.onopen = () => {
        console.log('[QuotaSync] Connected to quota stream');
        reconnectAttempts.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const data: QuotaData = JSON.parse(event.data);
          onQuotaUpdate(data);
        } catch (error) {
          console.error('[QuotaSync] Error parsing quota data:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('[QuotaSync] Connection error:', error);

        // Close the connection
        eventSource.close();
        eventSourceRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`[QuotaSync] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          console.error('[QuotaSync] Max reconnection attempts reached');
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('[QuotaSync] Error creating EventSource:', error);
    }
  }, [enabled, onQuotaUpdate]);

  const disconnect = useCallback(() => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close EventSource connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      console.log('[QuotaSync] Disconnected from quota stream');
    }

    reconnectAttempts.current = 0;
  }, []);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return { disconnect };
};
